import { MAX_LAYOUT_DEPTH, type AuthoringBlockNode } from '@personalizehq/schemas';
import { newId } from './doc.js';

// Tree ops over the block tree. Every one of these is written to run INSIDE an immer draft
// (see session.ts `applyChange`) — they mutate, and the store turns that into a patch pair.
//
// `hit` is the only walker. Everything else is expressed in terms of it, so there is exactly one
// place that knows how a parent/child relationship is discovered.

/** The four containers, i.e. the blocks with a `children[]` the author can insert into.
 *  `repeat` is NOT one: its `template` is a single slot, and authoring inside it is a later slice
 *  (p3-authoring-dashboard.md §3). It still gets its ids rewritten by `duplicateNode`. */
export type ContainerNode = Extract<AuthoringBlockNode, { children: AuthoringBlockNode[] }>;

export function isContainer(node: AuthoringBlockNode): node is ContainerNode {
  return (
    node.block === 'stack' ||
    node.block === 'box' ||
    node.block === 'scroll' ||
    node.block === 'grid'
  );
}

export interface Hit {
  node: AuthoringBlockNode;
  /** null for the root. */
  parent: ContainerNode | null;
  /** -1 for the root. */
  index: number;
}

/** Depth-first search of the EDITABLE tree — children only, never into a `fallback` or a
 *  `repeat.template`. Those are separate authoring surfaces (E5 / later slice), and letting a
 *  selection or a move land inside one from the layers tree would be a silent structural bug. */
export function findNode(root: AuthoringBlockNode, id: string): Hit | undefined {
  if (root.id === id) return { node: root, parent: null, index: -1 };
  if (!isContainer(root)) return undefined;

  for (let i = 0; i < root.children.length; i++) {
    const child = root.children[i]!;
    if (child.id === id) return { node: child, parent: root, index: i };
    const deeper = findNode(child, id);
    if (deeper) return deeper;
  }
  return undefined;
}

/** Every id in the subtree, INCLUDING the ones inside `fallback` and `repeat.template` — those
 *  are real nodes on the wire, so a duplicate must not clone their ids. */
export function collectIds(node: AuthoringBlockNode, into: Set<string> = new Set()): Set<string> {
  into.add(node.id);
  if (isContainer(node)) for (const c of node.children) collectIds(c, into);
  if (node.block === 'repeat') collectIds(node.template, into);
  if (node.fallback) collectIds(node.fallback, into);
  return into;
}

export function isDescendant(root: AuthoringBlockNode, ancestorId: string, maybeDescendantId: string): boolean {
  const hit = findNode(root, ancestorId);
  if (!hit) return false;
  if (ancestorId === maybeDescendantId) return true;
  return collectIds(hit.node).has(maybeDescendantId);
}

/** Editable ids in deterministic depth-first document order. */
export function editableNodeIds(root: AuthoringBlockNode): string[] {
  const ids: string[] = [];
  const walk = (node: AuthoringBlockNode): void => {
    ids.push(node.id);
    if (isContainer(node)) node.children.forEach(walk);
  };
  walk(root);
  return ids;
}

/** Drop missing ids, duplicates, and descendants whose selected ancestor already carries them. */
export function normalizeTopLevelSelection(root: AuthoringBlockNode, ids: readonly string[]): string[] {
  const selected = new Set(ids.filter((id) => Boolean(findNode(root, id))));
  return editableNodeIds(root).filter((id) => {
    if (!selected.has(id)) return false;
    const path = pathTo(root, id);
    return path ? !path.slice(0, -1).some((ancestor) => selected.has(ancestor.id)) : false;
  });
}

export function insertNode(root: AuthoringBlockNode, parentId: string, index: number, node: AuthoringBlockNode): void {
  const hit = findNode(root, parentId);
  if (!hit) throw new Error(`insert: unknown parent ${parentId}`);
  if (!isContainer(hit.node)) throw new Error(`insert: ${hit.node.block} is not a container`);
  const at = clampIndex(index, hit.node.children.length);
  hit.node.children.splice(at, 0, node);
}

export function removeNode(root: AuthoringBlockNode, id: string): void {
  const hit = findNode(root, id);
  if (!hit) throw new Error(`remove: unknown node ${id}`);
  if (!hit.parent) throw new Error('remove: the root cannot be removed');
  hit.parent.children.splice(hit.index, 1);
}

export function removeNodes(root: AuthoringBlockNode, ids: readonly string[]): string[] {
  const normalized = normalizeTopLevelSelection(root, ids);
  if (normalized.includes(root.id)) throw new Error('remove: the root cannot be removed');
  const removedIds = new Set(normalized.flatMap((id) => [...collectIds(findNode(root, id)!.node)]));
  let strandedTrack: string | undefined;
  walkAll(root, (node) => {
    if (node.block === 'progress' && node.tracks && !removedIds.has(node.id) && removedIds.has(node.tracks)) {
      strandedTrack = node.id;
    }
  });
  if (strandedTrack) throw new Error(`remove: progress “${strandedTrack}” tracks a selected scroll`);
  // Removing rightmost/deepest nodes first keeps every preflighted sibling index valid.
  const ordered = normalized
    .map((id) => ({ id, path: pathTo(root, id)! }))
    .sort((a, b) => b.path.length - a.path.length || (findNode(root, b.id)?.index ?? 0) - (findNode(root, a.id)?.index ?? 0));
  ordered.forEach(({ id }) => removeNode(root, id));
  return normalized;
}

/** Move within the tree. The two guards are the whole reason this is not two lines of splice:
 *  a node cannot move into its own subtree (it would vanish from the doc), and removing it from
 *  its old slot SHIFTS the target index when both are in the same parent. */
export function moveNode(root: AuthoringBlockNode, id: string, newParentId: string, index: number): void {
  const hit = findNode(root, id);
  if (!hit) throw new Error(`move: unknown node ${id}`);
  if (!hit.parent) throw new Error('move: the root cannot be moved');
  if (isDescendant(root, id, newParentId)) throw new Error('move: cannot move into own subtree');

  const target = findNode(root, newParentId);
  if (!target) throw new Error(`move: unknown parent ${newParentId}`);
  if (!isContainer(target.node)) throw new Error(`move: ${target.node.block} is not a container`);

  const sameParent = target.node === hit.parent;
  let at = clampIndex(index, target.node.children.length);
  if (sameParent && hit.index < at) at -= 1;

  hit.parent.children.splice(hit.index, 1);
  target.node.children.splice(at, 0, hit.node);
}

/** Move a normalized selection as one contiguous document-ordered group. Validation precedes writes. */
export function moveNodes(root: AuthoringBlockNode, ids: readonly string[], newParentId: string, index: number): string[] {
  const normalized = normalizeTopLevelSelection(root, ids);
  if (normalized.length === 0) return [];
  if (normalized.includes(root.id)) throw new Error('move: the root cannot be moved');
  const target = findNode(root, newParentId);
  if (!target) throw new Error(`move: unknown parent ${newParentId}`);
  if (!isContainer(target.node)) throw new Error(`move: ${target.node.block} is not a container`);
  for (const id of normalized) {
    if (isDescendant(root, id, newParentId)) throw new Error('move: cannot move into own subtree');
    const subtree = findNode(root, id)!.node;
    if (pathTo(root, newParentId)!.length + subtreeDepth(subtree) > MAX_LAYOUT_DEPTH) {
      throw new Error(`move: would exceed the ${MAX_LAYOUT_DEPTH}-level depth limit`);
    }
  }

  const hits = normalized.map((id) => findNode(root, id)!);
  let at = clampIndex(index, target.node.children.length);
  at -= hits.filter((hit) => hit.parent === target.node && hit.index < at).length;

  const byParent = new Map<ContainerNode, Set<string>>();
  hits.forEach((hit) => {
    const parent = hit.parent!;
    const set = byParent.get(parent) ?? new Set<string>();
    set.add(hit.node.id);
    byParent.set(parent, set);
  });
  byParent.forEach((removed, parent) => {
    parent.children = parent.children.filter((child) => !removed.has(child.id));
  });
  target.node.children.splice(clampIndex(at, target.node.children.length), 0, ...hits.map((hit) => hit.node));
  return normalized;
}

/** Insert a fresh-id deep clone directly after the original. Ids are regenerated across the WHOLE
 *  subtree — children, `fallback`s and `repeat.template`s alike — because a duplicated id is a
 *  duplicated `data-phq-id`, and the renderer's hit-testing and the conformance snapshot both key
 *  on it. */
export function duplicateNode(root: AuthoringBlockNode, id: string): AuthoringBlockNode {
  const hit = findNode(root, id);
  if (!hit) throw new Error(`duplicate: unknown node ${id}`);
  if (!hit.parent) throw new Error('duplicate: the root cannot be duplicated');

  const clone = cloneWithFreshIds(hit.node);
  hit.parent.children.splice(hit.index + 1, 0, clone);
  return clone;
}

export function duplicateNodes(root: AuthoringBlockNode, ids: readonly string[]): AuthoringBlockNode[] {
  const normalized = normalizeTopLevelSelection(root, ids);
  if (normalized.includes(root.id)) throw new Error('duplicate: the root cannot be duplicated');
  const hits = normalized.map((id) => findNode(root, id)!);
  const { clones } = cloneSubtreesWithFreshIds(hits.map((hit) => hit.node));

  // Insert per parent from right to left so every original index remains stable.
  const insertions = hits.map((hit, i) => ({ hit, clone: clones[i]! }));
  insertions
    .slice()
    .sort((a, b) => b.hit.index - a.hit.index)
    .forEach(({ hit, clone }) => hit.parent!.children.splice(hit.index + 1, 0, clone));
  return clones;
}

/** Replace one non-root node with an empty container at the same sibling index, then place the
 *  original node inside it. The caller creates the wrapper (and therefore owns its defaults/theme),
 *  while this operation owns the structural invariants and remains one atomic immer mutation. */
export function wrapNode(root: AuthoringBlockNode, id: string, wrapper: AuthoringBlockNode): ContainerNode {
  const hit = findNode(root, id);
  if (!hit) throw new Error(`wrap: unknown node ${id}`);
  if (!hit.parent) throw new Error('wrap: the root cannot be wrapped');
  if (!isContainer(wrapper)) throw new Error(`wrap: ${wrapper.block} is not a container`);
  if (wrapper.children.length > 0) throw new Error('wrap: the wrapper must be empty');
  if (collectIds(root).has(wrapper.id)) throw new Error(`wrap: duplicate wrapper id ${wrapper.id}`);
  if (pathTo(root, id)!.length + subtreeDepth(hit.node) > MAX_LAYOUT_DEPTH) {
    throw new Error(`wrap: would exceed the ${MAX_LAYOUT_DEPTH}-level depth limit`);
  }

  wrapper.children.push(hit.node);
  hit.parent.children.splice(hit.index, 1, wrapper);
  return wrapper;
}

/** Wrap the closest-common-ancestor branches contributed by a selection. */
export function wrapNodes(root: AuthoringBlockNode, ids: readonly string[], wrapper: AuthoringBlockNode): ContainerNode {
  const normalized = normalizeTopLevelSelection(root, ids);
  if (normalized.length === 0) throw new Error('wrap: selection is empty');
  if (normalized.length === 1) return wrapNode(root, normalized[0]!, wrapper);
  if (normalized.includes(root.id)) throw new Error('wrap: the root cannot be wrapped');
  if (!isContainer(wrapper)) throw new Error(`wrap: ${wrapper.block} is not a container`);
  if (wrapper.children.length > 0) throw new Error('wrap: the wrapper must be empty');
  if (collectIds(root).has(wrapper.id)) throw new Error(`wrap: duplicate wrapper id ${wrapper.id}`);

  const paths = normalized.map((id) => pathTo(root, id));
  if (paths.some((path) => !path)) throw new Error('wrap: unknown selection');
  const resolved = paths as AuthoringBlockNode[][];
  let commonLength = 0;
  while (resolved.every((path) => path[commonLength]?.id === resolved[0]![commonLength]?.id)) commonLength += 1;
  const common = resolved[0]![commonLength - 1];
  if (!common || !isContainer(common)) throw new Error('wrap: selection has no container ancestor');

  const branches = new Set(
    resolved.map((path) => path[commonLength]!.id)
  );
  const children = common.children.filter((child) => branches.has(child.id));
  if (children.length === 0) throw new Error('wrap: selection has no common branches');
  if (pathTo(root, common.id)!.length + 1 + Math.max(...children.map(subtreeDepth)) > MAX_LAYOUT_DEPTH) {
    throw new Error(`wrap: would exceed the ${MAX_LAYOUT_DEPTH}-level depth limit`);
  }
  const at = common.children.findIndex((child) => child.id === children[0]!.id);
  common.children = common.children.filter((child) => !branches.has(child.id));
  wrapper.children.push(...children);
  common.children.splice(at, 0, wrapper);
  return wrapper;
}

export function cloneWithFreshIds(node: AuthoringBlockNode): AuthoringBlockNode {
  return cloneSubtreesWithFreshIds([node]).clones[0]!;
}

/** Clone several roots through one id map so references between copied roots can be repaired. */
export function cloneSubtreesWithFreshIds(nodes: readonly AuthoringBlockNode[]): {
  clones: AuthoringBlockNode[];
  idMap: ReadonlyMap<string, string>;
} {
  // ponytail: the IR is JSON by definition and JSON cloning works through immer draft proxies.
  const clones = JSON.parse(JSON.stringify(nodes)) as AuthoringBlockNode[];
  const idMap = new Map<string, string>();
  const assign = (node: AuthoringBlockNode): void => {
    const old = node.id;
    node.id = newId();
    idMap.set(old, node.id);
    if (isContainer(node)) node.children.forEach(assign);
    if (node.block === 'repeat') assign(node.template);
    if (node.fallback) assign(node.fallback);
  };
  clones.forEach(assign);

  const repair = (node: AuthoringBlockNode): void => {
    if (node.block === 'progress' && node.tracks && idMap.has(node.tracks)) node.tracks = idMap.get(node.tracks)!;
    if (isContainer(node)) node.children.forEach(repair);
    if (node.block === 'repeat') repair(node.template);
    if (node.fallback) repair(node.fallback);
  };
  clones.forEach(repair);
  return { clones, idMap };
}

function pathTo(root: AuthoringBlockNode, id: string): AuthoringBlockNode[] | undefined {
  if (root.id === id) return [root];
  if (!isContainer(root)) return undefined;
  for (const child of root.children) {
    const path = pathTo(child, id);
    if (path) return [root, ...path];
  }
  return undefined;
}

function clampIndex(index: number, length: number): number {
  if (!Number.isFinite(index)) return length;
  return Math.max(0, Math.min(Math.trunc(index), length));
}

function subtreeDepth(node: AuthoringBlockNode): number {
  let depth = 1;
  walkAll(node, (_child, level) => { depth = Math.max(depth, level); });
  return depth;
}

function walkAll(node: AuthoringBlockNode, visit: (node: AuthoringBlockNode, level: number) => void, level = 1): void {
  visit(node, level);
  if (isContainer(node)) node.children.forEach((child) => walkAll(child, visit, level + 1));
  if (node.block === 'repeat') walkAll(node.template, visit, level + 1);
  if (node.fallback) walkAll(node.fallback, visit, level + 1);
}
